/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import {extendTheme} from '@salesforce/retail-react-app/app/components/shared/ui'

// Foundational style overrides
import styles from './foundation/styles'
import colors from './foundation/colors'
import fonts from './foundation/fonts'
import gradients from '@salesforce/retail-react-app/app/theme/foundations/gradients'
import sizes from './foundation/sizes'
import space from '@salesforce/retail-react-app/app/theme/foundations/space'
import layerStyles from './foundation/layerStyles'
import shadows from '@salesforce/retail-react-app/app/theme/foundations/shadows'

// Base component style overrides
import Alert from '@salesforce/retail-react-app/app/theme/components/base/alert'
import Accordion from '@salesforce/retail-react-app/app/theme/components/base/accordion'
import Badge from '@salesforce/retail-react-app/app/theme/components/base/badge'

import Button from './components/base/button'
import Checkbox from './components/base/checkbox'
import Link from './components/base/link'
import Container from '@salesforce/retail-react-app/app/theme/components/base/container'
import Drawer from '@salesforce/retail-react-app/app/theme/components/base/drawer'
import FormLabel from './components/base/formLabel'
import Icon from '@salesforce/retail-react-app/app/theme/components/base/icon'
import Input from './components/base/input'
import Modal from './components/base/modal'
import Radio from '@salesforce/retail-react-app/app/theme/components/base/radio'
import Select from '@salesforce/retail-react-app/app/theme/components/base/select'
import Skeleton from '@salesforce/retail-react-app/app/theme/components/base/skeleton'
import Tooltip from '@salesforce/retail-react-app/app/theme/components/base/tooltip'
import Popover from './components/base/popover'

// Project Component style overrides
import App from './components/project/_app'
import Breadcrumb from '@salesforce/retail-react-app/app/theme/components/project/breadcrumb'
import Header from './components/project/header'
import Minicart from './components/project/minicart'
import Suggestions from './components/project/suggestions'
import ListMenu from './components/project/list-menu'
import EmptySearchResults from './components/project/emptyResults'
import FilterSwatches from './components/project/filterSwatches'
import Footer from './components/project/footer'
import FormatedContentAsset from './components/project/formated-content-asset'
import CheckoutFooter from '@salesforce/retail-react-app/app/theme/components/project/checkout-footer'
import LinksList from '@salesforce/retail-react-app/app/theme/components/project/links-list'
import DrawerMenu from '@salesforce/retail-react-app/app/theme/components/project/drawer-menu'
import DisplayPrice from './components/project/display-price'
import NestedAccordion from '@salesforce/retail-react-app/app/theme/components/project/nested-accordion'
import LocaleSelector from '@salesforce/retail-react-app/app/theme/components/project/locale-selector'
import OfflineBanner from '@salesforce/retail-react-app/app/theme/components/project/offline-banner'
import Pagination from '@salesforce/retail-react-app/app/theme/components/project/pagination'
import ProductTile from './components/project/product-tile'
import ProductSashes from './components/project/product-sashes'
import SocialIcons from '@salesforce/retail-react-app/app/theme/components/project/social-icons'
import SwatchGroup from './components/project/swatch-group'
import ImageGallery from '@salesforce/retail-react-app/app/theme/components/project/image-gallery'
import AccountPayments from './components/project/account-payments'
import CheckoutStripePayment from './components/project/checkout-stripe-payment'
import PowerReviewOverride from './components/project/power-review-override'
import PowerReviewProductPage from './components/project/power-review-pdp'
import SlickImageOverride from './components/project/slick-image-override'
import GiftCertificate from './components/project/gift-certificate'
import countDownTimer from './components/project/countDownTimer'
import FilterSection from './components/project/filter-section'
import ProductAvailability from './components/project/product-availability'

import ImagetileWithOverlay from './components/project/image-tile-with-overlay'
import ShopBySeriesLayoutStyles from './components/project/shopBySeriesGrid.js'
import TrendingProducts from './components/project/textTileWithProductilesContainerStyle.js'
import ProductView from './components/project/product-view.js'
import SubscribeToSeries from './components/project/subscribe-to-series'

import containerStyles from './components/project/containerStyles'
import shopTheLookProductTile from './components/project/shopTheLookProductTile'
import promotionalContentTileBanner from './components/project/promotionalContentTileBanner.js'
import linkBanner from './components/project/linkBanner'

import CheckoutglobalStyles from './components/project/checkout-global-styles.js'
import CartStyles from './components/project/cart-styles.js'
import QuantityPickerStyles from './components/project/quantity-picker'
import OrderSummaryStyles from './components/project/order-summary'

// Please refer to the Chakra-Ui theme customization docs found
// here https://chakra-ui.com/docs/theming/customize-theme to learn
// more about extending and overriding themes for your project.

// Custom focus ring color
const customFocusColor = '0 0 1px 3px #FAB818'

export const overrides = {
    styles: {
        global: {
            ...styles.global,
            button: {
                _focusVisible: {
                    boxShadow: `${customFocusColor} !important`
                }
            },
            input: {
                _focusVisible: {
                    boxShadow: `${customFocusColor} !important`
                }
            },
            select: {
                _focusVisible: {
                    boxShadow: `${customFocusColor} !important`
                }
            },
            textarea: {
                _focusVisible: {
                    boxShadow: `${customFocusColor} !important`
                }
            },
            'a:focus, .slick-arrow:focus, .product-hero-image-section [tabindex]:focus': {
                boxShadow: customFocusColor,
                outline: 'none'
            },
            '.p-w-r :focus, .p-w-r a:focus': {
                boxShadow: `${customFocusColor} !important`,
                outline: 'none'
            }
        }
    },
    layerStyles,
    colors,
    sizes,
    space,
    gradients,
    shadows: {
        ...shadows,
        outline: customFocusColor
    },
    fonts,
    components: {
        // base components
        Accordion,
        Alert,
        Badge,
        Button,
        Checkbox,
        Link,
        Container,
        Drawer,
        FormLabel,
        Icon,
        Input,
        Modal,
        Popover,
        Radio,
        Select,
        Skeleton,
        Tooltip,

        // project components
        App,
        Breadcrumb,
        EmptySearchResults,
        Header,
        Minicart,
        FilterSwatches,
        Footer,
        FormatedContentAsset,
        CheckoutFooter,
        LinksList,
        ListMenu,
        DrawerMenu,
        DisplayPrice,
        NestedAccordion,
        LocaleSelector,
        OfflineBanner,
        SocialIcons,
        Suggestions,
        Pagination,
        ProductTile,
        ProductSashes,
        SwatchGroup,
        AccountPayments,
        CheckoutStripePayment,
        ImageGallery,
        PowerReviewOverride,
        PowerReviewProductPage,
        SlickImageOverride,
        GiftCertificate,
        countDownTimer,
        FilterSection,
        ProductAvailability,
        CheckoutglobalStyles,
        CartStyles,
        QuantityPickerStyles,
        OrderSummaryStyles,

        // pd components
        ImagetileWithOverlay,
        ShopBySeriesLayoutStyles,
        TrendingProducts,
        ProductView,
        SubscribeToSeries,
        containerStyles,
        shopTheLookProductTile,
        promotionalContentTileBanner,
        linkBanner
    },
    breakpoints: {
        base: '0px', // 767px to 0px
        sm: '768px', // 1023px to 768px
        md: '1024px', // 1199px to 1024px
        lg: '1200px', // 1399px to 1200px
        xl: '1440px', // 1919px to 1440px
        '2xl': '1920px' // >=1920px
    },
    fontSizes: {
        fontSize28: '28px',
        fontSize32: '32px'
    },
    Image: {
        baseStyle: {
            transition: 'opacity 0.5s ease'
        }
    }
}

export default extendTheme(overrides)
